import { CellContext, ColumnDef } from '@tanstack/react-table';

import { Modify } from '@/model/common';
import { FXSwaps, Instrument, InstrumentResponse } from '@/types/sso';
import { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import { createDateSortingFn } from '@/utils/table';
import BigNumber from 'bignumber.js';

export interface FXSwapRow
    extends Modify<
        FXSwaps,
        {
            formattedBid: string;
            formattedAsk: string;
            pipBid: number;
            tableTradeDate: string;
            pipAsk: number;
        }
    > {}

export interface InstrumentRow
    extends Modify<
        Instrument,
        {
            tableSymbol: string;
            tablePositionUTIPattern: string;
            tableTradeUTIPattern: string;
            tableCcy1Network: string;
            tableCcy1NetworkType: string;
            tableStartDate: string;
            tableEndDate: string;
            tableMarginRequirement: string;
        }
    > {}

interface GetFxSwapColumnsParams {
    lang: UseLanguageReturn;
}

export const getFxSwapColumns = ({ lang }: GetFxSwapColumnsParams) => {
    const columns: ColumnDef<FXSwapRow>[] = [
        {
            size: 125,
            accessorKey: 'tableTradeDate',
            header: lang.portal.tradeDate,
            cell: (info: CellContext<FXSwapRow, any>) => info.getValue(),
            sortingFn: createDateSortingFn<FXSwapRow>((row) => row.original.tradeDate)
        },
        {
            size: 125,
            accessorKey: 'currencyPair',
            header: lang.portal.instrumentName,
            cell: (info: CellContext<FXSwapRow, any>) => info.getValue()
        },
        {
            accessorKey: 'formattedBid',
            header: lang.portal.bidRate,
            cell: (info: CellContext<FXSwapRow, any>) => info.getValue()
        },
        {
            accessorKey: 'formattedAsk',
            header: lang.portal.askRate,
            cell: (info: CellContext<FXSwapRow, any>) => info.getValue()
        }
    ];

    return columns;
};

export const convertToFxSwapsRow = (
    fxSwaps: FXSwaps,
    instruments: InstrumentResponse[],
    dateFormatter: Intl.DateTimeFormat,
    locale: string
): FXSwapRow => {
    const config = instruments?.find((instrument) => instrument.symbol == fxSwaps.currencyPair);

    const pipConfig = BigNumber(10).pow(-(config?.pipsDecimals ?? 0));

    const pipBid = BigNumber(fxSwaps.bid).multipliedBy(pipConfig);
    const pipAsk = BigNumber(fxSwaps.ask).multipliedBy(pipConfig);

    const numberFormatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: config?.swapDecimals ?? 0,
        maximumFractionDigits: config?.swapDecimals ?? 0
    });

    return {
        ...fxSwaps,
        tableTradeDate: dateFormatter.format(new Date(fxSwaps.tradeDate)),
        formattedBid: config ? numberFormatter.format(pipBid.toNumber()) : `(Unformatted) ${fxSwaps.bid}`,
        formattedAsk: config ? numberFormatter.format(pipAsk.toNumber()) : `(Unformatted) ${fxSwaps.ask}`,
        pipBid: pipBid.toNumber(),
        pipAsk: pipAsk.toNumber()
    };
};
